/**
 * Trip Feature Manager Service
 *
 * Manages the creation, editing, styling, and organization of trip features
 * for FEAT-010 Interactive Trip Feature Drawing and Management.
 */

import { logger } from '../utils/logger.js'
import { db } from '../database/schema.js'
import type { Feature, FeatureStyle, FeatureLabel } from '../schemas/feature.js'
import { sanitizeUserInput, validateUserInputSecurity } from '../storage/validation.js'
import { featureStorage } from '../storage/feature-storage.js'
import { selectionService } from './selection/selection-service.js'
import { authService } from './auth/auth-service.js'
import { layerGroupManager } from './layer-group-manager.js'

const log = logger.create('trips')

export type MapMode = 'map' | 'tracking' | 'draw' | 'select' | 'measure'
export type DrawSubContext = 'tools' | 'focused' | 'extending'

export interface LatLng {
  lat: number
  lng: number
}

export interface Bounds {
  ne: LatLng
  sw: LatLng
}

export interface CreateFeatureData {
  name: string
  type: 'point' | 'line' | 'polygon' | 'circle' | 'rectangle'
  geometry: GeoJSON.Geometry
  description?: string | undefined
  style?: FeatureStyle | undefined
  label?: FeatureLabel | undefined
  properties?: Record<string, unknown> | undefined
  tags?: string[] | undefined
}

/**
 * Scoping context for the feature manager. When set, features are loaded from
 * and created within a specific entity (e.g. a route).
 */
export interface FeatureScope {
  entityType: string
  entityId: string
  /** Default tags to apply per feature type when the user hasn't specified any */
  defaultTags?: Record<string, string[]>
}

export class TripFeatureManager {
  private static instance: TripFeatureManager | undefined
  private currentDrawingMode: 'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | null = null
  private _mapMode: MapMode = 'map'
  private _drawSubContext: DrawSubContext = 'tools'
  private selectedFeatureIds: string[] = []
  private focusedFeatureId: string | null = null
  private lastCreatedFeature?: Feature | undefined
  private extendingFeatureId: string | null = null
  private extendDirection: 'append' | 'prepend' | null = null
  private hasLocationPermission: boolean = false
  private _scope: FeatureScope | null = null

  static getInstance(): TripFeatureManager {
    TripFeatureManager.instance ??= new TripFeatureManager()
    return TripFeatureManager.instance
  }

  // ===== Scope Management =====
  setScope(scope: FeatureScope | null): void {
    this._scope = scope
  }

  getScope(): FeatureScope | null {
    return this._scope
  }

  // ===== Mode Management =====
  getMapMode(): MapMode {
    return this._mapMode
  }

  setMapMode(mode: MapMode): void {
    if (mode === 'map') {
      this.currentDrawingMode = null
      this._drawSubContext = 'tools'
      this.focusedFeatureId = null
      this.extendingFeatureId = null
      selectionService.clearBySource('user')
      this.selectedFeatureIds = []
    } else if (mode === 'draw') {
      this._drawSubContext = 'tools'
      this.focusedFeatureId = null
      this.extendingFeatureId = null
      selectionService.clearBySource('user')
      this.selectedFeatureIds = []
    } else if (mode === 'select') {
      this.currentDrawingMode = null
      this._drawSubContext = 'tools'
      this.focusedFeatureId = null
      this.extendingFeatureId = null
    }
    this._mapMode = mode
  }

  getDrawSubContext(): DrawSubContext {
    return this._drawSubContext
  }

  setLocationPermission(hasPermission: boolean): void {
    this.hasLocationPermission = hasPermission
  }

  getLocationPermission(): boolean {
    return this.hasLocationPermission
  }

  // Legacy compatibility - map old context modes to new system
  getContextMode(): 'location' | 'drawing' | 'editing' | 'extending' {
    if (this._mapMode === 'map') {
      return 'location'
    }
    if (this._mapMode === 'select') {
      return 'editing'
    }
    if (this._mapMode === 'draw') {
      if (this._drawSubContext === 'focused') {
        return 'editing'
      }
      if (this._drawSubContext === 'extending') {
        return 'extending'
      }
      return 'drawing'
    }
    return 'location'
  }

  setContextMode(mode: 'location' | 'drawing' | 'editing' | 'extending'): void {
    if (mode === 'location') {
      this.setMapMode('map')
    } else if (mode === 'drawing') {
      this._mapMode = 'draw'
      this._drawSubContext = 'tools'
    } else if (mode === 'editing') {
      this._drawSubContext = 'focused'
    } else {
      this._drawSubContext = 'extending'
    }
  }

  private getDefaultContextMode(): 'location' | 'drawing' {
    return this.hasLocationPermission ? 'location' : 'drawing'
  }

  // ===== Drawing Tool Management =====
  selectDrawingTool(tool: 'point' | 'line' | 'polygon' | 'circle' | 'rectangle'): void {
    this.currentDrawingMode = tool
    this._mapMode = 'draw'
    this._drawSubContext = 'tools'
    this.focusedFeatureId = null
  }

  getSelectedTool(): string | null {
    return this.currentDrawingMode
  }

  clearDrawingTool(): void {
    this.currentDrawingMode = null
  }

  clearDrawingMode(): void {
    this.currentDrawingMode = null
    if (this.extendingFeatureId === null) {
      this.setContextMode(this.getDefaultContextMode())
    }
  }

  // ===== Feature Creation =====
  private async getNextFeatureNumber(type: string): Promise<number> {
    const existing = await db.features.where('type').equals(type).count()
    return existing + 1
  }

  async createPointFeature(
    location: LatLng,
    options: Partial<CreateFeatureData> = {}
  ): Promise<Feature> {
    const geometry: GeoJSON.Point = {
      type: 'Point',
      coordinates: [location.lng, location.lat],
    }

    const featureData: CreateFeatureData = {
      name: options.name ?? `Point ${await this.getNextFeatureNumber('point')}`,
      type: 'point',
      geometry,
      description: options.description,
      style: options.style ?? this.getDefaultStyle('point'),
      label: options.label,
      properties: options.properties ?? {},
      tags: options.tags,
    }

    return this.createFeature(featureData)
  }

  async createLineFeature(
    points: LatLng[],
    options: Partial<CreateFeatureData> = {}
  ): Promise<Feature> {
    const coordinates = points.map(p => [p.lng, p.lat])
    const geometry: GeoJSON.LineString = {
      type: 'LineString',
      coordinates,
    }

    const featureData: CreateFeatureData = {
      name: options.name ?? `Line ${await this.getNextFeatureNumber('line')}`,
      type: 'line',
      geometry,
      description: options.description,
      style: options.style ?? this.getDefaultStyle('line'),
      label: options.label,
      properties: options.properties ?? {},
      tags: options.tags,
    }

    return this.createFeature(featureData)
  }

  async createPolygonFeature(
    points: LatLng[],
    options: Partial<CreateFeatureData> = {}
  ): Promise<Feature> {
    const coordinates = points.map(p => [p.lng, p.lat])
    // Close the polygon by adding the first point at the end (GeoJSON requirement)
    coordinates.push(coordinates[0])
    const geometry: GeoJSON.Polygon = {
      type: 'Polygon',
      coordinates: [coordinates],
    }

    const featureData: CreateFeatureData = {
      name: options.name ?? `Polygon ${await this.getNextFeatureNumber('polygon')}`,
      type: 'polygon',
      geometry,
      description: options.description,
      style: options.style ?? this.getDefaultStyle('polygon'),
      label: options.label,
      properties: options.properties ?? {},
      tags: options.tags,
    }

    return this.createFeature(featureData)
  }

  async createCircleFeature(
    center: LatLng,
    radiusMeters: number,
    options: Partial<CreateFeatureData> = {}
  ): Promise<Feature> {
    // Store as GeoJSON Point with radius in properties
    const geometry: GeoJSON.Point = {
      type: 'Point',
      coordinates: [center.lng, center.lat],
    }

    const featureData: CreateFeatureData = {
      name: options.name ?? `Circle ${await this.getNextFeatureNumber('circle')}`,
      type: 'circle',
      geometry,
      description: options.description,
      style: { ...this.getDefaultStyle('circle'), radius: radiusMeters },
      label: options.label,
      properties: { radius: radiusMeters, ...options.properties },
      tags: options.tags,
    }

    return this.createFeature(featureData)
  }

  async createRectangleFeature(
    bounds: Bounds,
    options: Partial<CreateFeatureData> = {}
  ): Promise<Feature> {
    const { ne, sw } = bounds
    const coordinates = [
      [
        [sw.lng, sw.lat], // SW corner
        [ne.lng, sw.lat], // SE corner
        [ne.lng, ne.lat], // NE corner
        [sw.lng, ne.lat], // NW corner
        [sw.lng, sw.lat], // Close the rectangle
      ],
    ]

    const geometry: GeoJSON.Polygon = {
      type: 'Polygon',
      coordinates,
    }

    const featureData: CreateFeatureData = {
      name: options.name ?? `Rectangle ${await this.getNextFeatureNumber('rectangle')}`,
      type: 'rectangle',
      geometry,
      description: options.description,
      style: options.style ?? this.getDefaultStyle('rectangle'),
      label: options.label,
      properties: options.properties ?? {},
      tags: options.tags,
    }

    return this.createFeature(featureData)
  }

  /** Resolve tags: explicit > scope defaults > empty */
  private resolveDefaultTags(data: CreateFeatureData): string[] {
    if (data.tags && data.tags.length > 0) return data.tags
    if (this._scope?.defaultTags) {
      return this._scope.defaultTags[data.type] ?? []
    }
    return []
  }

  private async createFeature(data: CreateFeatureData): Promise<Feature> {
    const sanitizedName = sanitizeUserInput(data.name)
    const sanitizedDescription = data.description ? sanitizeUserInput(data.description) : undefined

    const nameSecurityCheck = validateUserInputSecurity(sanitizedName)
    if (!nameSecurityCheck.isValid) {
      throw new Error(
        `Security validation failed for name: ${nameSecurityCheck.errors[0]?.message}`
      )
    }

    if (sanitizedDescription) {
      const descSecurityCheck = validateUserInputSecurity(sanitizedDescription)
      if (!descSecurityCheck.isValid) {
        throw new Error(
          `Security validation failed for description: ${descSecurityCheck.errors[0]?.message}`
        )
      }
    }

    const resolvedTags = this.resolveDefaultTags(data)

    // Scoped mode: use featureStorage for edges
    if (this._scope) {
      const result = await featureStorage.createFeature(
        {
          name: sanitizedName,
          type: data.type,
          geometry: data.geometry,
          description: sanitizedDescription,
          style: data.style,
          label: data.label,
          properties: data.properties ?? {},
          tags: resolvedTags,
          isVisible: true,
          createdBy: authService.getCurrentUserId(),
        },
        {
          entityType: this._scope.entityType,
          entityId: this._scope.entityId,
        }
      )

      if (!result.success || !result.data) {
        throw new Error(`Feature creation failed: ${JSON.stringify(result.error)}`)
      }

      // Update bounds after creation (featureStorage doesn't compute bounds)
      const bounds = this.calculateFeatureBounds(data.geometry)
      if (bounds) {
        await db.features.update(result.data.id, { bounds })
        result.data.bounds = bounds
      }

      this.lastCreatedFeature = result.data
      return result.data
    }

    // Global mode: wire to personal Features root group
    const userId = authService.getCurrentUserId()
    const featuresRoot = await layerGroupManager.ensureFeaturesRoot()

    const result = await featureStorage.createFeature(
      {
        name: sanitizedName,
        type: data.type,
        geometry: data.geometry,
        description: sanitizedDescription,
        style: data.style,
        label: data.label,
        properties: data.properties ?? {},
        tags: resolvedTags,
        isVisible: true,
        createdBy: userId,
      },
      {
        entityType: 'user',
        entityId: userId,
        groupId: featuresRoot.id,
      }
    )

    if (!result.success || !result.data) {
      throw new Error(`Feature creation failed: ${JSON.stringify(result.error)}`)
    }

    const bounds = this.calculateFeatureBounds(data.geometry)
    if (bounds) {
      await db.features.update(result.data.id, { bounds })
      result.data.bounds = bounds
    }

    this.lastCreatedFeature = result.data
    return result.data
  }

  // ===== Feature Management =====
  async getFeature(featureId: string): Promise<Feature | undefined> {
    return await db.features.get(featureId)
  }

  async getAllFeatures(): Promise<Feature[]> {
    if (this._scope) {
      return featureStorage.getFeaturesForEntity(this._scope.entityType, this._scope.entityId)
    }
    return await db.features.toArray()
  }

  async getLastCreatedFeature(): Promise<Feature> {
    if (!this.lastCreatedFeature) {
      throw new Error('No feature has been created yet')
    }
    return this.lastCreatedFeature
  }

  async deleteFeature(featureId: string): Promise<void> {
    await featureStorage.deleteFeature(featureId)
  }

  async updateFeatureName(featureId: string, name: string): Promise<void> {
    const sanitizedName = sanitizeUserInput(name)
    const validation = validateUserInputSecurity(sanitizedName)
    if (!validation.isValid) {
      throw new Error(`Security validation failed for name: ${validation.errors[0]?.message}`)
    }
    await db.features.update(featureId, {
      name: sanitizedName,
      updatedAt: new Date().toISOString(),
    })
  }

  // ===== Feature Styling =====
  async applyFeatureStyle(featureId: string, style: FeatureStyle): Promise<void> {
    await db.features.update(featureId, { style })
  }

  async updateFeatureStyle(
    featureId: string,
    styleUpdates: Partial<FeatureStyle>
  ): Promise<Feature | undefined> {
    const feature = await this.getFeature(featureId)
    if (!feature) {
      return undefined
    }
    const merged = { ...(feature.style ?? {}), ...styleUpdates }
    await db.features.update(featureId, {
      style: merged,
      updatedAt: new Date().toISOString(),
    })
    return await this.getFeature(featureId)
  }

  async getFeatureStyle(featureId: string): Promise<FeatureStyle | undefined> {
    const feature = await this.getFeature(featureId)
    return feature?.style
  }

  async addFeatureDescription(featureId: string, description: string): Promise<void> {
    await db.features.update(featureId, { description })
  }

  async addFeatureLabel(featureId: string, label: FeatureLabel): Promise<void> {
    await db.features.update(featureId, { label })
  }

  // ===== Focus Management (Draw Mode - single shape editing) =====
  focusFeature(featureId: string): void {
    this.focusedFeatureId = featureId
    this._mapMode = 'draw'
    this._drawSubContext = 'focused'
  }

  unfocusFeature(): void {
    this.focusedFeatureId = null
    this._drawSubContext = 'tools'
  }

  getFocusedFeatureId(): string | null {
    return this.focusedFeatureId
  }

  // ===== Selection Management (Select Mode - bulk operations) =====
  selectFeature(featureId: string): void {
    selectionService.select({ source: 'user', featureId })
    this.selectedFeatureIds = selectionService.getSelectedUserFeatureIds()
    if (this._mapMode === 'draw') {
      this.focusFeature(featureId)
    } else {
      this._mapMode = 'select'
    }
  }

  toggleFeatureSelection(featureId: string): void {
    selectionService.toggle({ source: 'user', featureId })
    this.selectedFeatureIds = selectionService.getSelectedUserFeatureIds()
    if (this.selectedFeatureIds.includes(featureId)) {
      this._mapMode = 'select'
    }
  }

  removeFromSelection(featureId: string): void {
    selectionService.deselect({ source: 'user', featureId })
    this.selectedFeatureIds = selectionService.getSelectedUserFeatureIds()
  }

  clearSelection(): void {
    selectionService.clearAll()
    this.selectedFeatureIds = []
    this.focusedFeatureId = null
    if (this._mapMode === 'draw') {
      this._drawSubContext = 'tools'
    }
  }

  getSelectedFeatures(): string[] {
    return selectionService.getSelectedUserFeatureIds()
  }

  // ===== Extend Mode Management =====
  setExtendMode(featureId: string): void {
    this.extendingFeatureId = featureId
    this.extendDirection = null // not yet chosen — user must click an endpoint
    this._mapMode = 'draw'
    this._drawSubContext = 'extending'
    this.currentDrawingMode = null
  }

  setExtendDirection(direction: 'append' | 'prepend'): void {
    this.extendDirection = direction
  }

  getExtendDirection(): 'append' | 'prepend' | null {
    return this.extendDirection
  }

  getExtendingFeatureId(): string | null {
    return this.extendingFeatureId
  }

  isExtending(): boolean {
    return this.extendingFeatureId !== null && this._drawSubContext === 'extending'
  }

  /** Direction has been chosen and user is actively adding points */
  isActivelyExtending(): boolean {
    return this.isExtending() && this.extendDirection !== null
  }

  exitExtendMode(): void {
    this.extendingFeatureId = null
    this.extendDirection = null
    this._drawSubContext = 'focused'
  }

  // ===== Geometry Editing =====
  async moveFeature(featureId: string, newLocation: LatLng): Promise<void> {
    const feature = await this.getFeature(featureId)
    if (!feature) {
      return
    }

    if (feature.type === 'point' || feature.type === 'circle') {
      const geometry: GeoJSON.Point = {
        type: 'Point',
        coordinates: [newLocation.lng, newLocation.lat],
      }
      await db.features.update(featureId, {
        geometry,
        bounds: this.calculateFeatureBounds(geometry),
        updatedAt: new Date().toISOString(),
      })
      log.debug('🔧 ✅ Feature center updated in database:', feature.type, newLocation)
    } else if (feature.type === 'rectangle') {
      // For rectangles, calculate the offset and move all corners
      const coords = (feature.geometry as GeoJSON.Polygon).coordinates[0] // Rectangle as polygon
      const corners = coords.slice(0, -1) // Remove duplicate last point

      // Calculate current center
      const currentCenterLat =
        corners.reduce((sum: number, coord: number[]) => sum + coord[1], 0) / corners.length
      const currentCenterLng =
        corners.reduce((sum: number, coord: number[]) => sum + coord[0], 0) / corners.length

      // Calculate offset
      const offsetLat = newLocation.lat - currentCenterLat
      const offsetLng = newLocation.lng - currentCenterLng

      // Move all corners by the offset
      const newCorners = corners.map((coord: number[]) => [
        coord[0] + offsetLng, // lng
        coord[1] + offsetLat, // lat
      ])

      // Close the rectangle
      const closedCorners = [...newCorners, newCorners[0]]

      const geometry: GeoJSON.Polygon = {
        type: 'Polygon',
        coordinates: [closedCorners],
      }

      await db.features.update(featureId, {
        geometry,
        bounds: this.calculateFeatureBounds(geometry),
        updatedAt: new Date().toISOString(),
      })
      log.debug('🔧 ✅ Rectangle center updated in database:', newLocation)
    }
  }

  async extendLineToPoint(featureId: string, newPoint: LatLng): Promise<void> {
    const feature = await this.getFeature(featureId)
    if (feature?.type !== 'line') {
      throw new Error('Feature is not a line or does not exist')
    }

    const coordinates = [...(feature.geometry as GeoJSON.LineString).coordinates]
    if (this.extendDirection === 'prepend') {
      coordinates.unshift([newPoint.lng, newPoint.lat])
    } else {
      coordinates.push([newPoint.lng, newPoint.lat])
    }

    const newGeometry: GeoJSON.LineString = {
      type: 'LineString',
      coordinates,
    }

    await db.features.update(featureId, {
      geometry: newGeometry,
      bounds: this.calculateFeatureBounds(newGeometry),
      updatedAt: new Date().toISOString(),
    })
  }

  // ===== Helper Methods =====
  private getDefaultStyle(type: string): FeatureStyle {
    switch (type) {
      case 'point':
        return {
          pointColor: '#3f5a7a',
          pointSize: 12,
          pointOpacity: 1.0,
        }
      case 'line':
        return {
          lineColor: '#3f5a7a',
          lineWidth: 3,
          lineOpacity: 0.8,
          linePattern: 'solid',
        }
      case 'polygon':
      case 'rectangle':
        return {
          fillColor: '#3f5a7a',
          fillOpacity: 0.3,
          strokeColor: '#3f5a7a',
          strokeWidth: 2,
          strokeOpacity: 0.8,
        }
      case 'circle':
        return {
          fillColor: '#3f5a7a',
          fillOpacity: 0.3,
          strokeColor: '#3f5a7a',
          strokeWidth: 2,
          strokeOpacity: 0.8,
        }
      default:
        return {}
    }
  }

  private calculateFeatureBounds(
    geometry: GeoJSON.Geometry
  ): { ne: [number, number]; sw: [number, number] } | undefined {
    const geom = geometry as GeoJSON.Geometry & { coordinates?: unknown }
    if (!geom.coordinates) {
      return undefined
    }

    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity

    const processCoordinate = (coord: number[]): void => {
      const [lng, lat] = coord
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
    }

    const processCoordinates = (coords: unknown): void => {
      if (Array.isArray(coords) && Array.isArray(coords[0])) {
        coords.forEach((coord: unknown) => processCoordinates(coord))
      } else if (Array.isArray(coords)) {
        processCoordinate(coords as number[])
      }
    }

    if (geometry.type === 'Point') {
      processCoordinate(geometry.coordinates)
    } else {
      processCoordinates(geom.coordinates)
    }

    if (minLng === Infinity) {
      return undefined
    }

    return {
      ne: [maxLat, maxLng],
      sw: [minLat, minLng],
    }
  }

  // ===== Cleanup =====
  async clearAllData(): Promise<void> {
    await db.features.clear()
    selectionService.clearAll()
    this.selectedFeatureIds = []
    this.currentDrawingMode = null
    this._mapMode = 'map'
    this._drawSubContext = 'tools'
    this.focusedFeatureId = null
    this.lastCreatedFeature = undefined
  }
}

export const tripFeatureManager = TripFeatureManager.getInstance()
